import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {Meeting} from '../../../shared/interface/meeting';
import {MeetingService} from '../../../shared/services/meeting.service';
import {takeUntil} from 'rxjs/operators';
import {ListResponse} from '../../../shared/interface/response/list-response';
import {OfwcaMeeting} from '../../../shared/interface/ofwca-meeting';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {
  MEETING_STATUS_DURING_REGISTRATIONS, MEETING_TYPE_ENTRANCE_EXAM,
  MEETING_TYPE_EXAM,
  MEETING_TYPE_IDD_TRAINING, MEETING_TYPE_NEW_AGENT_TRAINING
} from '../../../shared/services/dict.service';
import {HttpErrorResponse} from '@angular/common/http';
import {Uuid} from '../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {ContractInsuranceCompany} from "../../../shared/interface/contract-insurance-company";
import {Sort} from "@angular/material/sort";

export interface OfwcaMeetingForm {
  uuid: string;
  ofwcaMeeting: OfwcaMeeting;
  form: FormGroup;
}

@Component({
  selector: 'app-registered-ofwcas-list',
  templateUrl: './registered-ofwcas-list.component.html',
  styleUrls: ['./registered-ofwcas-list.component.scss']
})
export class RegisteredOfwcasListComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  readonly MEETING_STATUS_DURING_REGISTRATIONS = MEETING_STATUS_DURING_REGISTRATIONS;
  readonly MEETING_TYPE_EXAM = MEETING_TYPE_EXAM;
  readonly MEETING_TYPE_ENTRANCE_EXAM = MEETING_TYPE_ENTRANCE_EXAM;
  readonly MEETING_TYPE_IDD_TRAINING = MEETING_TYPE_IDD_TRAINING;
  readonly MEETING_TYPE_NEW_AGENT_TRAINING = MEETING_TYPE_NEW_AGENT_TRAINING;

  @Input() meeting: Observable<Meeting>;
  @Output() changed: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('importSelect') importSelect: ElementRef;

  meetingObject: Meeting;
  ofwcaMeetingDataSource: MatTableDataSource<OfwcaMeetingForm> = new MatTableDataSource<OfwcaMeetingForm>([]);
  displayedColumns: string[] = [];
  forms: OfwcaMeetingForm[] = [];
  protocolOpened = false;
  processingRequest = false;
  fileImportSelectControl: FormControl;
  selectAllControl: FormControl;
  allPassedIddTrainingControl: FormControl;
  allPassedTestControl: FormControl;
  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = 'ofwcaLastName';
  sortOrder = 'desc';

  constructor(
    private meetingService: MeetingService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private fb: FormBuilder,
    private auth: AuthService,
  ) {
    this.fileImportSelectControl = fb.control('');
    this.selectAllControl = fb.control(false);
    this.allPassedTestControl = fb.control(false);
    this.allPassedIddTrainingControl = fb.control(false);
    this.userPermissions = auth.getUserPermissions();
  }

  ngOnInit(): void {
    this.meeting
      .pipe(takeUntil(this.destroyed$))
      .subscribe((meeting: Meeting) => {
        this.meetingObject = meeting;

        if (meeting.type.key === MEETING_TYPE_EXAM || meeting.type.key === MEETING_TYPE_ENTRANCE_EXAM) {
          this.displayedColumns = ['export', 'ofwca', 'ofwcaLastName', 'pesel', 'partner',
            'ofwcaAssetsRegional', 'partnerAssetsRegional', 'activeKrk', 'points1', 'points2', 'passedExam', 'actions', ];
        }

        if (meeting.type.key === MEETING_TYPE_IDD_TRAINING) {
          this.displayedColumns = ['ofwca', 'partner',
            'ofwcaAssetsRegional', 'partnerAssetsRegional', 'passedTest',  'passedIddTraining', 'attachedCertificate',
            'sentAt', 'actions', ];
        }
        if (meeting.type.key === MEETING_TYPE_NEW_AGENT_TRAINING) {
          this.sortBy = 'dateOfRegistrationForNewAgentTraining';
          this.sortOrder = 'asc';
          this.displayedColumns = ['ofwca', 'dateOfRegistrationForNewAgentTraining' , 'status', 'partner',
            'ofwcaAssetsRegional', 'partnerAssetsRegional',  'actions', ];
        }
        this.loadRegisteredOfwcas();
      });

    this.fileImportSelectControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val: any) => {
        if (!this.importSelect.nativeElement.files[0]) {
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(this.importSelect.nativeElement.files[0]);
        reader.onload = () => {
          const fileBase64 = reader.result.toString();

          this.processingRequest = true;
          this.meetingService.importData(this.meetingObject.uuid, /base64,(.+)/.exec(fileBase64)[1])
            .subscribe(() => {
              this.processingRequest = false;
              this.snack.open('Import zakończony sukcesem', '', {duration: 2000});
              this.loadRegisteredOfwcas();
            }, (error: HttpErrorResponse) => {
              this.processingRequest = false;
              this.snack.open('Przy importowaniu wystąpił błąd', error.status.toString(), {duration: 2000});
            });
        };
      });

    this.selectAllControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((checked: boolean) => {
        this.forms.forEach((form: OfwcaMeetingForm) => {
          form.form.get('export').setValue(checked );
        });
      });

    this.allPassedIddTrainingControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((checked: boolean) => {
        this.forms.forEach((form: OfwcaMeetingForm) => {
          form.form.get('passed').setValue(checked);
        });
      });

    this.allPassedTestControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((checked: boolean) => {
        this.forms.forEach((form: OfwcaMeetingForm) => {
          form.form.get('passedTest').setValue(checked);
        });
      });
  }

  loadRegisteredOfwcas(): void {
    this.meetingService.getRegisteredOfwcas(this.meetingObject.uuid, {
      sortBy: this.sortBy,
      sortOrder: this.sortOrder
    })
      .subscribe((response: ListResponse<OfwcaMeeting>) => {
        this.forms = [];
        response.items.forEach((ofwcaMeeting: OfwcaMeeting) => {
          this.forms.push({
            uuid: ofwcaMeeting.uuid,
            ofwcaMeeting,
            form: this.fb.group({
              export: this.fb.control(false),
              points1: this.fb.control(ofwcaMeeting.points1),
              points2: this.fb.control(ofwcaMeeting.points2),
              passedTest: this.fb.control(ofwcaMeeting.passedTest),
              passed: this.fb.control(ofwcaMeeting.passed)
            })
          });
        });

        this.ofwcaMeetingDataSource.data = this.forms;
      });
  }

  ngAfterViewInit(): void {
    this.ofwcaMeetingDataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  deregisterOfwca(ofwcaMeeting: OfwcaMeeting): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz wypisać OFWCA z egzaminu?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.meetingService.deregisterOfwca(ofwcaMeeting.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie wypisano OFWCA', '', {duration: 2000});
            this.changed.emit();
          });
      }
    });
  }

  saveForms(): void {
    let saved = 0;
    this.processingRequest = true;
    this.forms.forEach((ofwcaMeetingForm: OfwcaMeetingForm) => {
      setTimeout(() =>
      this.meetingService.updateOfwcaMeeting(ofwcaMeetingForm.uuid, ofwcaMeetingForm.form.value)
        .subscribe(() => {
          saved++;

          if (saved === this.forms.length) {
            this.snack.open('Zmiany zostały zapisane', '', {duration: 2000});
            this.protocolOpened = false;
            this.changed.emit();
            this.processingRequest = false;
          }
        }), 5000 );
    });
  }

  openProtocol(): void {
    this.protocolOpened = true;
  }

  importData(): void {
    const e: HTMLElement = this.importSelect.nativeElement;
    e.click();
  }

  cancel(): void {
    this.protocolOpened = false;
    this.loadRegisteredOfwcas();
  }

  downloadReport(): void {
    this.meetingService.downloadMeetingReport(this.meetingObject.uuid, {
      sortOrder: this.sortOrder
    })
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  sendToInsuranceCompany(): void {
    const ofwcaIds = [];
    let allPassed = true;
    this.forms.forEach((form: OfwcaMeetingForm) => {
      if (form.form.get('export').value) {
        ofwcaIds.push(form.ofwcaMeeting.ofwca.uuid);
        if (!form.ofwcaMeeting.passed)
        {
          allPassed = false;
        }
      }
    });
    if (!allPassed)
    {
      this.snack.open('Nie można wysłać do rejestracji OFWCA, która nie zdała egzaminu', '', {duration: 2000});
      return;
    }
    if (ofwcaIds.length === 0) {
      this.snack.open('Aby wysłać rejestracje i wygenerować dokumenty najpierw zaznacz OFWCA', '', {duration: 2000});
      return;
    }

    this.meetingService.generateExamDocuments(this.meetingObject.uuid, ofwcaIds, true)
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });

        dialogRef.afterClosed().subscribe(result => {
        });
      });
  }

  downloadCertificate(ofwcaMeeting: OfwcaMeeting): void {
    this.meetingService.downloadCertificate(ofwcaMeeting.uuid);
  }

  downloadCertificates(): void {
    this.meetingService.downloadCertificates(this.meetingObject.uuid)
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });

        dialogRef.afterClosed().subscribe(result => {
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  sendCertificate(ofwcaMeeting: OfwcaMeeting): void {
    this.meetingService.sendCertificate(ofwcaMeeting.uuid)
      .subscribe(() => {
          this.snack.open('Certyfikat został wysłany', '', {duration: 2000});
        }, (error: HttpErrorResponse) => {
          this.snack.open('Przy wysyłąniu certyfikatu wystąpił błąd', error.status.toString(), {duration: 2000});
        });
  }

  sendCertificates(): void {
    let i = 0;
    this.forms.forEach((form: OfwcaMeetingForm) => {
          if (form.ofwcaMeeting.iddTrainingCertificate) {
            i = i + 1;
          }else{
          }
        });
        if (i > 0){
          this.meetingService.sendCertificates(this.meetingObject.uuid)
            .subscribe(() => {
              this.snack.open('Certyfikaty zostały wysłane', '', {duration: 2000});
            }, (error: HttpErrorResponse) => {
              this.snack.open('Przy wysyłąniu certyfikatów wystąpił błąd', error.status.toString(), {duration: 2000});
            });
        }else{
          this.snack.open('Brak certyfikatów do wysłania', '', {duration: 2000});
        }
  }

  downloadDocuments(): void {
    const ofwcaIds = [];
    this.forms.forEach((form: OfwcaMeetingForm) => {
      if (form.form.get('export').value) {
        ofwcaIds.push(form.ofwcaMeeting.ofwca.uuid);
      }
    });
    if (ofwcaIds.length === 0) {
      this.snack.open('Aby wysłać rejestracje i wygenerować dokumenty najpierw zaznacz OFWCA', '', {duration: 2000});
      return;
    }
    this.meetingService.generateExamDocuments(this.meetingObject.uuid, ofwcaIds, false)
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });

        dialogRef.afterClosed().subscribe(result => {
        });
      });
  }

  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;
    this.loadRegisteredOfwcas();
  }
}
